import React from "react";
import { Link } from "react-router-dom";
import {
  Truck, Globe2, FileCheck2, Car, KeyRound, ArrowRight,
  Layers, MapPin, ShieldCheck, Handshake, Award,
  Globe, HeartHandshake, Ship, Building2, HardHat, Landmark,
  Compass, Zap,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SectionTitle from "@/components/SectionTitle";
import FinalCTA from "@/components/FinalCTA";
import { IMAGES, SERVICES, WHY_ABAYLE, CLIENTS, COMPANY } from "@/lib/content";

const ICONS = {
  Truck, Globe2, FileCheck2, Car, KeyRound, Layers, MapPin, ShieldCheck,
  Handshake, Award, Globe, HeartHandshake, Ship, Building2, HardHat, Landmark,
  Compass, Zap,
};

const Hero = () => (
  <section className="relative isolate overflow-hidden min-h-[88vh] sm:min-h-[90vh] lg:min-h-[92vh] flex items-center" data-testid="hero">
    <img
      src={IMAGES.hero}
      alt="Abayle port and logistics operations"
      className="absolute inset-0 h-full w-full object-cover"
    />
    <div className="absolute inset-0 bg-abayle-green-darker/70" />
    <div className="absolute inset-0 bg-gradient-to-r from-abayle-green-darker/85 via-abayle-green-dark/60 to-abayle-green/40" />

    <div className="relative container-abayle py-20 sm:py-28 lg:py-40 text-white">
      <div className="max-w-3xl">
        <div className="mb-5 inline-flex items-center gap-3 animate-reveal">
          <span className="h-px w-10 bg-abayle-gold" />
          <span className="text-abayle-gold uppercase text-[10px] sm:text-xs tracking-[0.25em] sm:tracking-[0.3em] font-semibold">
            Abayle Logistics and Trading • Mogadishu, Somalia
          </span>
        </div>
        <h1 className="text-3xl sm:text-5xl lg:text-[64px] font-bold tracking-tight leading-[1.1] sm:leading-[1.05] animate-reveal" style={{ animationDelay: "0.1s" }} data-testid="hero-headline">
          Integrated <span className="text-abayle-gold">Logistics</span>, Global Trade, Clearance, Car Sales &amp; Car Rental Solutions in Somalia
        </h1>
        <p className="mt-5 sm:mt-6 text-white/85 max-w-2xl text-sm sm:text-base lg:text-lg leading-relaxed animate-reveal" style={{ animationDelay: "0.2s" }}>
          Abayle Logistics and Trading supports businesses, institutions, and field operations through dependable logistics coordination, trade support, clearance services, car sales, and car rental solutions designed for the Somali market.
        </p>
        <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4 animate-reveal" style={{ animationDelay: "0.3s" }}>
          <Link to="/services" className="btn-primary-abayle bg-abayle-gold border-abayle-gold text-abayle-green hover:bg-white hover:border-white" data-testid="hero-cta-services">
            Explore Services
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/contact" className="btn-hero-outline" data-testid="hero-cta-contact">
            Contact Us
          </Link>
        </div>

        {/* Stats strip */}
        <div className="mt-12 sm:mt-16 grid grid-cols-3 gap-3 sm:gap-6 max-w-xl animate-reveal" style={{ animationDelay: "0.4s" }}>
          {[
            { num: "5", label: "Core Services" },
            { num: "78", label: "Trusted Partner" },
            { num: "24/7", label: "Client Support" },
          ].map((s) => (
            <div key={s.label} className="border-l-2 border-abayle-gold/70 pl-3 sm:pl-4">
              <div className="text-2xl sm:text-3xl lg:text-4xl font-bold text-abayle-gold">{s.num}</div>
              <div className="text-[10px] sm:text-xs uppercase tracking-wider sm:tracking-widest text-white/75 mt-1 leading-tight">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>

    <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-abayle-gold" />
  </section>
);

const FiveCoreStrip = () => (
  <section className="bg-white" data-testid="five-core-strip">
    <div className="container-abayle py-14 sm:py-20 lg:py-24">
      <SectionTitle
        centered
        eyebrow="Our Capabilities"
        title="Five Core Services. One Trusted Partner."
        subtitle="Abayle brings together five essential service lines under one professional brand, helping clients reduce delays, simplify coordination, and work with confidence."
      />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-5">
        {SERVICES.map((s) => {
          const Icon = ICONS[s.icon];
          return (
            <Link
              key={s.key}
              to={`/services#${s.key}`}
              className="card-abayle text-center flex flex-col items-center gap-4 py-8"
              data-testid={`service-chip-${s.key}`}
            >
              <div className="h-14 w-14 rounded-full bg-abayle-ivory-soft flex items-center justify-center border border-abayle-gold/40 group-hover:bg-abayle-green transition-colors">
                <Icon className="h-6 w-6 text-abayle-green" />
              </div>
              <h3 className="text-abayle-green font-bold text-base tracking-wide">{s.title}</h3>
            </Link>
          );
        })}
      </div>
    </div>
  </section>
);

const AboutSnapshot = () => (
  <section className="bg-abayle-ivory" data-testid="about-snapshot">
    <div className="container-abayle py-14 sm:py-20 lg:py-28 grid lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-20 items-center">
      <div className="relative">
        <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-abayle-gold z-10" />
        <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-abayle-gold z-10" />
        <img
          src={IMAGES.aboutPort}
          alt="Port and cargo coordination"
          className="relative w-full h-[280px] sm:h-[420px] lg:h-[520px] object-cover rounded-sm shadow-xl"
        />
      </div>
      <div>
        <SectionTitle
          eyebrow="About Abayle"
          title="A Trusted Somali Partner Built for Movement, Trade, and Client Confidence"
        />
        <p className="text-abayle-charcoal/85 text-base lg:text-lg leading-relaxed">
          Abayle Logistics and Trading is a professionally managed Somali company headquartered in Mogadishu and positioned to support clients across cargo movement, trade support, clearance processes, car sales, and car rental services. The company combines local market understanding, responsive communication, and practical execution to serve both institutional and commercial clients.
        </p>
        <p className="mt-4 text-abayle-charcoal/80 leading-relaxed">
          Abayle is a trusted Somali partner operating through an integrated, multi-service platform.
        </p>
        <Link to="/about" className="btn-primary-abayle mt-8" data-testid="about-readmore">
          Read More About Us
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </div>
  </section>
);

const ServicesOverview = () => (
  <section className="bg-white" data-testid="services-overview">
    <div className="container-abayle py-14 sm:py-20 lg:py-28">
      <SectionTitle
        centered
        eyebrow="What We Do"
        title="Five Services. One Integrated Approach."
        subtitle="From cargo movement to client mobility — every service is built around reliability, local knowledge, and responsive support."
      />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {SERVICES.map((s, i) => {
          const Icon = ICONS[s.icon];
          return (
            <div key={s.key} className={`card-abayle group ${i === 2 ? "lg:col-span-1" : ""}`} data-testid={`service-card-${s.key}`}>
              <div className="flex items-center gap-4 mb-5">
                <div className="h-12 w-12 rounded-md bg-abayle-green/5 border border-abayle-green/15 flex items-center justify-center group-hover:bg-abayle-green group-hover:border-abayle-green transition-all">
                  <Icon className="h-5 w-5 text-abayle-green group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-abayle-green font-bold text-xl">{s.title}</h3>
              </div>
              <p className="text-abayle-charcoal/75 leading-relaxed text-[15px]">{s.short}</p>
              <Link
                to={`/services#${s.key}`}
                className="mt-6 inline-flex items-center gap-2 text-abayle-green font-semibold group/link"
                data-testid={`service-card-link-${s.key}`}
              >
                <span className="border-b border-abayle-gold group-hover/link:text-abayle-gold transition-colors">Learn more</span>
                <ArrowRight className="h-4 w-4 text-abayle-gold group-hover/link:translate-x-0.5 transition-transform" />
              </Link>
            </div>
          );
        })}
      </div>
      <div className="mt-12 text-center">
        <Link to="/services" className="btn-secondary-abayle" data-testid="services-viewall">
          View All Services
        </Link>
      </div>
    </div>
  </section>
);

const CarSolutionsHighlight = () => (
  <section className="bg-abayle-ivory-soft" data-testid="car-solutions-highlight">
    <div className="container-abayle py-14 sm:py-20 lg:py-28">
      <SectionTitle
        centered
        eyebrow="Car Solutions"
        title="Reliable Car Solutions for Operations and Mobility"
      />
      <div className="grid lg:grid-cols-2 gap-8">
        {[
          {
            key: "sales",
            title: "Car Sales",
            text: "Abayle provides practical car sales solutions for clients looking for dependable vehicles for business, project, and field use.",
            img: IMAGES.carSales,
            link: "/car-solutions#sales",
          },
          {
            key: "rental",
            title: "Car Rental",
            text: "Abayle offers car rental options for organizations, professionals, and visiting teams that need flexible, reliable mobility support in Somalia.",
            img: IMAGES.carRental,
            link: "/car-solutions#rental",
          },
        ].map((item) => (
          <Link
            key={item.key}
            to={item.link}
            className="group relative overflow-hidden rounded-lg bg-white shadow-sm hover:shadow-2xl transition-all duration-500"
            data-testid={`car-highlight-${item.key}`}
          >
            <div className="relative h-80 overflow-hidden">
              <img
                src={item.img}
                alt={item.title}
                className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-abayle-green-darker/90 via-abayle-green/20 to-transparent" />
              <div className="absolute top-0 left-0 h-1 w-0 group-hover:w-full bg-abayle-gold transition-all duration-500" />
            </div>
            <div className="p-8">
              <div className="eyebrow mb-3">Car {item.key === "sales" ? "Sales" : "Rental"}</div>
              <h3 className="text-abayle-green text-xl sm:text-2xl lg:text-3xl font-bold mb-3">{item.title}</h3>
              <p className="text-abayle-charcoal/80 leading-relaxed">{item.text}</p>
              <div className="mt-6 inline-flex items-center gap-2 text-abayle-green font-semibold">
                <span className="border-b border-abayle-gold">Explore</span>
                <ArrowRight className="h-4 w-4 text-abayle-gold group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  </section>
);

const WhyChoose = () => (
  <section className="bg-white" data-testid="why-choose">
    <div className="container-abayle py-14 sm:py-20 lg:py-28">
      <SectionTitle
        centered
        eyebrow="Our Edge"
        title="Why Clients Choose Abayle"
        subtitle="A single accountable partner, built around reliability, responsiveness, and practical service delivery."
      />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {WHY_ABAYLE.map((item, idx) => {
          const Icon = ICONS[item.icon];
          return (
            <div key={item.title} className="card-abayle group" data-testid={`why-card-${idx}`}>
              <div className="flex items-start gap-5">
                <div className="shrink-0 h-12 w-12 rounded-full bg-abayle-green text-white flex items-center justify-center border-2 border-abayle-gold/40 group-hover:border-abayle-gold transition-colors">
                  <Icon className="h-5 w-5" />
                </div>
                <div>
                  <div className="text-abayle-gold text-xs font-bold tracking-[0.2em] uppercase mb-1">0{idx + 1}</div>
                  <h3 className="text-abayle-green font-bold text-lg mb-2">{item.title}</h3>
                  <p className="text-abayle-charcoal/75 text-[15px] leading-relaxed">{item.text}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const ClientsServed = () => (
  <section className="bg-abayle-ivory" data-testid="clients-served">
    <div className="container-abayle py-14 sm:py-20 lg:py-28">
      <SectionTitle
        centered
        eyebrow="Who We Serve"
        title="Trusted by Institutions, Partners & Businesses"
        subtitle="Abayle serves a wide range of client groups operating in Somalia, including institutions, development partners, commercial enterprises, and project based operations."
      />
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {CLIENTS.map((c, idx) => {
          const Icon = ICONS[c.icon];
          return (
            <div key={c.title} className="card-abayle group" data-testid={`client-card-${idx}`}>
              <Icon className="h-7 w-7 text-abayle-gold mb-4 transition-transform group-hover:scale-110" />
              <h3 className="text-abayle-green font-bold text-base lg:text-lg mb-2 leading-snug">{c.title}</h3>
              <p className="text-abayle-charcoal/75 text-sm leading-relaxed">{c.text}</p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const Home = () => {
  return (
    <div className="min-h-screen flex flex-col bg-abayle-ivory">
      <Navbar />
      <main>
        <Hero />
        <FiveCoreStrip />
        <AboutSnapshot />
        <ServicesOverview />
        <CarSolutionsHighlight />
        <WhyChoose />
        <ClientsServed />
        <FinalCTA />
      </main>
      <Footer />
    </div>
  );
};

export default Home;
